import { useCallback, useMemo } from "react";
import { navigateTo } from "../../utils/navigation";
import styles from "./ReturnToAppButton.module.css";

type Platform = "android" | "ios" | "web";

const ANDROID_RETURN_URL = process.env.REACT_APP_ANDROID_RETURN_URL ?? "petsrwise://payment-success";
const IOS_RETURN_URL = process.env.REACT_APP_IOS_RETURN_URL ?? "petsrwise://payment-success";
// The browser checkout must remain on the website. Native deep links are only
// appropriate for the mobile application handoff.
const WEB_RETURN_URL = process.env.REACT_APP_WEB_RETURN_URL ?? "https://www.petsrwise.com/cuenta/pedidos";
const RETURN_TO_APP_MESSAGE_TYPE = "RETURN_TO_APP" as const;
const NATIVE_HANDOFF_DELAY_MS = 400;
const NAVIGATION_FALLBACK_DELAY_MS = 1500;

const appendReturnCode = (baseUrl: string, parameter: "bookingCode" | "orderCode", code?: string): string => {
  if (!code) {
    return baseUrl;
  }

  try {
    const url = new URL(baseUrl);
    url.searchParams.set(parameter, code);
    return url.toString();
  } catch {
    const separator = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${separator}${parameter}=${encodeURIComponent(code)}`;
  }
};

const orderDetailUrl = (baseUrl: string, orderCode?: string): string => {
  if (!orderCode) return baseUrl;

  try {
    const url = new URL(baseUrl);
    url.pathname = `${url.pathname.replace(/\/$/, "")}/${encodeURIComponent(orderCode)}`;
    return url.toString();
  } catch {
    return `${baseUrl.replace(/\/$/, "")}/${encodeURIComponent(orderCode)}`;
  }
};

const detectPlatform = (): Platform => {
  if (typeof navigator === "undefined") {
    return "web";
  }

  const userAgent = navigator.userAgent || navigator.vendor || "";

  if (/android/i.test(userAgent)) {
    return "android";
  }

  if (/iPad|iPhone|iPod/i.test(userAgent)) {
    return "ios";
  }

  return "web";
};

interface ReturnToAppButtonProps {
  label: string;
  className?: string;
  bookingCode?: string;
  orderCode?: string;
}

interface ReturnToAppMessage {
  type: typeof RETURN_TO_APP_MESSAGE_TYPE;
  returnUrl: string;
}

const notifyNativeApp = (message: ReturnToAppMessage): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  const payload = JSON.stringify(message);
  let handled = false;

  const reactNativeBridge = window.ReactNativeWebView?.postMessage;
  if (typeof reactNativeBridge === "function") {
    reactNativeBridge(payload);
    handled = true;
  }

  const webkitBridge = window.webkit?.messageHandlers?.returnToApp;
  if (typeof webkitBridge?.postMessage === "function") {
    webkitBridge.postMessage(payload);
    handled = true;
  }

  return handled;
};

const navigateWithFallback = (platform: Platform, targetUrl: string): void => {
  if (typeof window === "undefined") {
    return;
  }

  navigateTo(targetUrl);

  if (platform === "web" || typeof document === "undefined") {
    return;
  }

  const timeoutId = window.setTimeout(() => {
    navigateTo(WEB_RETURN_URL);
  }, NAVIGATION_FALLBACK_DELAY_MS);

  const handleVisibilityChange = () => {
    if (document.hidden) {
      window.clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    }
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);
};

const scheduleNativeFallback = (platform: Platform, targetUrl: string): void => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  const timeoutId = window.setTimeout(() => {
    navigateWithFallback(platform, targetUrl);
  }, NATIVE_HANDOFF_DELAY_MS);

  const handleVisibilityChange = () => {
    if (document.hidden) {
      window.clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    }
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);
};

export const ReturnToAppButton = ({ label, className, bookingCode, orderCode }: ReturnToAppButtonProps) => {
  const platform = useMemo(() => detectPlatform(), []);
  const returnCode = orderCode
    ? { parameter: "orderCode" as const, value: orderCode }
    : { parameter: "bookingCode" as const, value: bookingCode };
  const androidUrl = useMemo(
    () => appendReturnCode(ANDROID_RETURN_URL, returnCode.parameter, returnCode.value),
    [returnCode.parameter, returnCode.value],
  );
  const iosUrl = useMemo(
    () => appendReturnCode(IOS_RETURN_URL, returnCode.parameter, returnCode.value),
    [returnCode.parameter, returnCode.value],
  );
  const webUrl = useMemo(
    () => orderCode
      ? orderDetailUrl(WEB_RETURN_URL, orderCode)
      : appendReturnCode(WEB_RETURN_URL, returnCode.parameter, returnCode.value),
    [orderCode, returnCode.parameter, returnCode.value],
  );

  const handleClick = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    const targetUrl = platform === "android" ? androidUrl : platform === "ios" ? iosUrl : webUrl;
    const message: ReturnToAppMessage = { type: RETURN_TO_APP_MESSAGE_TYPE, returnUrl: targetUrl };

    if (platform !== "web" && notifyNativeApp(message)) {
      scheduleNativeFallback(platform, targetUrl);
      return;
    }

    navigateWithFallback(platform, targetUrl);
  }, [androidUrl, iosUrl, platform, webUrl]);

  const classNames = className ? `${styles.button} ${className}` : styles.button;

  return (
    <button
      type="button"
      className={classNames}
      onClick={handleClick}
      data-android-href={androidUrl}
      data-ios-href={iosUrl}
    >
      {label}
    </button>
  );
};
